import { NucleusVaultStateWithdraw } from '@/state/nucleusVault/types'
import { ColoredHeadingTypography } from '@/swell-ui/Typography/typographyPresets'
import styled, { css } from 'styled-components'
import { INucleusActiveWithdrawal } from '../../nucleusHooks'
import { FlexRow } from '@/swell-ui/FlexRow'
import { Token } from '@/types/tokens'
import { AsyncDiv } from '@/swell-ui/AsyncDiv'
import { useMediaQuery } from '@mui/material'
import {
  makeNucleusClaimSummary,
  makeNucleusVaultWithdrawalsSummary,
} from '../../nucleusFormatting'
import { ScrollableYArea } from '@/swell-ui/ScrollableArea'

const bodyMediumCSS = css`
  color: var(--Swell-White-50, #fff);
  /* Body/medium */
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 160%; /* 25.6px */
  letter-spacing: -0.48px;
`

const TOKEN_LOGO_SIZE = 22

export function NucleusClaimBreakdown({
  activeWithdrawal,
  vaultState,
  vaultToken,
  withdrawAssets,
  baseAsset,
}: {
  activeWithdrawal: INucleusActiveWithdrawal | undefined
  vaultState: NucleusVaultStateWithdraw | undefined
  vaultToken: Token
  withdrawAssets: Token[]
  baseAsset: Token
}) {
  const is700Up = useMediaQuery('(min-width:700px)')
  const is900Up = useMediaQuery('(min-width:900px)')

  if (!activeWithdrawal || activeWithdrawal?.status === 'NoActiveWithdrawal') {
    return null
  }

  const claimSummary = makeNucleusClaimSummary({
    activeWithdrawal,
    vaultToken,
    withdrawAssets,
    baseAsset,
  })
  const withdrawalsSummary = makeNucleusVaultWithdrawalsSummary({
    vaultState,
  })

  const withdrawingFrag = (
    <>
      <ColoredHeadingTypography>Withdrawing</ColoredHeadingTypography>
      <span>
        {claimSummary.withdrawingLogoURI && (
          <img
            src={claimSummary.withdrawingLogoURI}
            alt={claimSummary.withdrawingSymbol}
            width={TOKEN_LOGO_SIZE}
            height={TOKEN_LOGO_SIZE}
          />
        )}
        {claimSummary.withdrawing}
      </span>
    </>
  )

  const receivingFrag = (
    <>
      <ColoredHeadingTypography>Receiving</ColoredHeadingTypography>
      <span>
        {claimSummary.receivingLogoURI && (
          <img
            src={claimSummary.receivingLogoURI}
            alt={claimSummary.receivingSymbol}
            width={TOKEN_LOGO_SIZE}
            height={TOKEN_LOGO_SIZE}
          />
        )}
        {claimSummary.receiving}
      </span>
    </>
  )

  const solverFeeFrag = (
    <>
      <ColoredHeadingTypography>Solver Fee</ColoredHeadingTypography>
      <AsyncDiv loading={!withdrawalsSummary.solverFee}>
        {() => withdrawalsSummary.solverFee}
      </AsyncDiv>
    </>
  )

  const processingTimeFrag = (
    <>
      <ColoredHeadingTypography>Processing Time</ColoredHeadingTypography>
      <AsyncDiv loading={!withdrawalsSummary.processingTime}>
        {() => withdrawalsSummary.processingTime}
      </AsyncDiv>
    </>
  )

  const targetTokenPriceFrag = (
    <>
      <ColoredHeadingTypography>Target token price</ColoredHeadingTypography>
      <AsyncDiv loading={!claimSummary.targetTokenPrice}>
        {() => claimSummary.targetTokenPrice}
      </AsyncDiv>
    </>
  )

  const arrangement2Col = [
    withdrawingFrag,
    receivingFrag,
    targetTokenPriceFrag,
    solverFeeFrag,
    processingTimeFrag,
  ]

  const _arrangement4Col = [
    [withdrawingFrag, solverFeeFrag],
    [receivingFrag, processingTimeFrag],
    [targetTokenPriceFrag],
  ]
  const arrangement4Col: typeof arrangement2Col = []
  arrangement4Col.push(_arrangement4Col[0][0])
  arrangement4Col.push(_arrangement4Col[0][1])
  arrangement4Col.push(_arrangement4Col[1][0])
  arrangement4Col.push(_arrangement4Col[1][1])
  arrangement4Col.push(_arrangement4Col[2][0])

  let arrangement = arrangement2Col
  if (is700Up) {
    arrangement = arrangement4Col
  } else if (is900Up) {
    arrangement = arrangement2Col
  } else {
    arrangement = arrangement4Col
  }

  return (
    <Layout
      direction="column"
      align="flex-start"
      gap="12"
      justify="center"
      maxWidth="586px"
    >
      <span className="bm left">Summary</span>
      <ScrollableYArea containerType="flex">
        <Grid>{arrangement}</Grid>
      </ScrollableYArea>
    </Layout>
  )
}

const Layout = styled(FlexRow)`
  align-self: stretch;
  color: var(--Swell-White-50, #fff);
  /* Body/medium */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 160%; /* 25.6px */
  letter-spacing: -0.48px;

  span {
    display: flex;
    align-items: center;

    img {
      margin-right: 8px;
    }
  }

  .bm {
    ${bodyMediumCSS}
  }
  .left {
    align-self: start;
  }

  /* padding-right: 32px; */

  [aria-busy='false'] {
    min-width: unset;
  }

  ${ColoredHeadingTypography} {
    font-size: 12px;
    text-align: right;
    justify-self: end;

    ${({ theme }) => theme.breakpoints.up('sm')} {
      white-space: nowrap;
    }
  }
`

const Grid = styled.div`
  width: max-content;
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;

  @media (min-width: 440px) {
    flex-flow: unset;
    gap: unset;

    margin: 0 auto;
    display: grid;
    grid-template-columns: auto max-content;
    grid-column-gap: 26px;
    grid-row-gap: 12px;
    align-items: center;
  }

  @media (min-width: 700px) {
    grid-template-columns: auto max-content auto max-content;
  }

  @media (min-width: 900px) {
    grid-template-columns: auto max-content;
  }

  @media (min-width: 1100px) {
    grid-template-columns: auto max-content auto max-content;
  }
`
